import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropType from 'prop-types'
import { useForm } from 'react-hook-form'

import { extractNumber } from '@cred/shared/utils'
import Money from '@cred/react-basic-form-fields/hookForm/Money'
import Dropdown from '@cred/react-basic-form-fields/hookForm/Dropdown'

import CtaButton from 'components/cta-button'
import { loanPurposeOptions } from 'components/dropdown/dropdown-options'
import { validateLoanAmount } from 'common/validations/loan-amount'
import { minMaxLoanAmountWithBhg } from 'personal-loan/rate-table/validations'
import { fireGtmEvent } from 'utils/gtm'
import PoweredBy from 'components/powered-by'
import useActiveLenders from 'components/hooks/useActiveLenders'

import styles from './styles.module.scss'

const getUrl = (href, loanAmount, loanPurpose) => {
  let _href = href

  if (loanAmount) {
    _href += `&loanAmount=${extractNumber(loanAmount)}`
  }

  if (loanPurpose) {
    _href += `&loanPurpose=${loanPurpose}`
  }

  return _href
}

const PersonalLoanForm = (props) => {
  const { href, ctaText, widgetContext } = props

  const [url, setUrl] = useState(href)

  const formMethods = useForm({ mode: 'onChange' })
  const { watch, control, formState } = formMethods
  const disabled = !formState.isValid
  const [loanAmount, loanPurpose] = watch(['loanAmount', 'loanPurpose'])

  useEffect(() => {
    setUrl(getUrl(href, loanAmount, loanPurpose))
    fireGtmEvent('fieldChange', { ...widgetContext, loanAmount })
  }, [loanAmount, href])

  useEffect(() => {
    setUrl(getUrl(href, loanAmount, loanPurpose))
    fireGtmEvent('fieldChange', { ...widgetContext, loanPurpose })
  }, [loanPurpose, href])

  const availableLenders = useActiveLenders('personal')

  const isBhgAvailable = useMemo(
    () => availableLenders.some((lender) => lender.slug === 'bhg'),
    [availableLenders],
  )

  const minMaxLoanAmountValidation = useCallback(
    (value) => {
      return isBhgAvailable ? minMaxLoanAmountWithBhg(value) : validateLoanAmount(value)
    },
    [isBhgAvailable],
  )

  const onClick = (e) => {
    if (disabled) {
      e.preventDefault()
      return
    }

    fireGtmEvent('ctaClick', { ...widgetContext, loanPurpose, loanAmount })
  }

  return (
    <>
      <section className={styles.form}>
        <div className="loan-amount mb-8">
          <Money
            id="loanAmount"
            name="loanAmount"
            label="Loan amount"
            placeholder="Loan amount"
            control={control}
            controllerProps={{ rules: { validate: { minMaxLoanAmountValidation } } }}
            exposeValue
          />
        </div>
        <div className="loan-purpose mb-16">
          <Dropdown
            id="loanPurpose"
            name="loanPurpose"
            label="Loan purpose"
            placeholder="Loan purpose"
            control={control}
          >
            {loanPurposeOptions.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </Dropdown>
        </div>
      </section>
      <section className={styles.submit}>
        <CtaButton
          data-testid="cta-button"
          href={url}
          onClick={onClick}
          target="_blank"
          fullWidth
          showDisabled={disabled}
          analytics={{
            data: { text: ctaText, activated: !disabled },
          }}
        >
          {ctaText}
        </CtaButton>
        <PoweredBy className="mt-8" />
      </section>
    </>
  )
}

PersonalLoanForm.propTypes = {
  href: PropType.string.isRequired,
  widgetContext: PropType.object.isRequired,
  ctaText: PropType.string.isRequired,
}

export { PersonalLoanForm }

export default PersonalLoanForm
